import axios from 'axios';

const api = axios.create({ baseURL: '/api' });

api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

api.interceptors.response.use(
  r => r,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// ── Auth ──────────────────────────────────────
export const authAPI = {
  login:          d => api.post('/auth/login', d),
  register:       d => api.post('/auth/register', d),
  me:             () => api.get('/auth/me'),
  changePassword: d => api.patch('/auth/change-password', d),
  setLanguage:    l => api.patch('/auth/language', { language: l }),
};

// ── Dashboard ─────────────────────────────────
export const dashboardAPI = {
  stats:    () => api.get('/dashboard/stats'),
  revenue:  (months=6) => api.get(`/dashboard/revenue?months=${months}`),
  activity: () => api.get('/dashboard/activity'),
};

// ── Properties ────────────────────────────────
export const propertiesAPI = {
  getAll:  () => api.get('/properties'),
  getOne:  id => api.get(`/properties/${id}`),
  create:  d  => api.post('/properties', d),
  update:  (id,d) => api.put(`/properties/${id}`, d),
  delete:  id => api.delete(`/properties/${id}`),
};

// ── Apartments ────────────────────────────────
export const apartmentsAPI = {
  getAll:  p => api.get('/apartments', { params: p }),
  getOne:  id => api.get(`/apartments/${id}`),
  create:  d  => api.post('/apartments', d),
  update:  (id,d) => api.put(`/apartments/${id}`, d),
  delete:  id => api.delete(`/apartments/${id}`),
};

// ── Tenants ───────────────────────────────────
export const tenantsAPI = {
  getAll:  p => api.get('/tenants', { params: p }),
  getOne:  id => api.get(`/tenants/${id}`),
  create:  d  => api.post('/tenants', d),
  update:  (id,d) => api.put(`/tenants/${id}`, d),
  delete:  id => api.delete(`/tenants/${id}`),
};

// ── Leases ────────────────────────────────────
export const leasesAPI = {
  getAll:  p      => api.get('/leases', { params: p }),
  getOne:  id     => api.get(`/leases/${id}`),
  create:  fd     => api.post('/leases', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update:  (id,d) => api.put(`/leases/${id}`, d, d instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {}),
  delete:  id     => api.delete(`/leases/${id}`),
  renew:   id     => api.post(`/leases/${id}/renew`),
};

// ── Payments ──────────────────────────────────
const multipart = { headers: { 'Content-Type': 'multipart/form-data' } };
export const paymentsAPI = {
  getAll:    p => api.get('/payments', { params: p }),
  getOne:    id => api.get(`/payments/${id}`),
  create:    fd => api.post('/payments', fd, multipart),
  update:    (id,d) => api.put(`/payments/${id}`, d),
  delete:    id => api.delete(`/payments/${id}`),
  markPaid:  (id,d) => api.patch(`/payments/${id}/mark-paid`, d),
  monthlySummary: () => api.get('/payments/summary/monthly'),
};

// ── Work Orders ───────────────────────────────
export const workOrdersAPI = {
  getAll:  p => api.get('/workorders', { params: p }),
  getOne:  id => api.get(`/workorders/${id}`),
  create:  fd => api.post('/workorders', fd, multipart),
  update:  (id,d) => api.put(`/workorders/${id}`, d),
  delete:  id => api.delete(`/workorders/${id}`),
};

// ── Inventory ─────────────────────────────────
export const inventoryAPI = {
  getAll:      p  => api.get('/inventory', { params: p }),
  getOne:      id => api.get(`/inventory/${id}`),
  create:      d  => api.post('/inventory', d),
  update:      (id,d) => api.put(`/inventory/${id}`, d),
  delete:      id => api.delete(`/inventory/${id}`),
  unitReport:       apartmentId => api.get(`/inventory/report/unit/${apartmentId}`),
  batch:            d           => api.post('/inventory/batch', d),
  addAttachments:   (id, fd)    => api.post(`/inventory/${id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteAttachment: (id, key)   => api.delete(`/inventory/${id}/attachments/${encodeURIComponent(key)}`),
};

// ── Inspection Reports ────────────────────────
export const inspectionsAPI = {
  getAll:  p  => api.get('/inspections', { params: p }),
  getOne:  id => api.get(`/inspections/${id}`),
  create:  d  => api.post('/inspections', d),
};

// ── Notifications ─────────────────────────────
export const notificationsAPI = {
  getAll:    p => api.get('/notifications', { params: p }),
  markRead:  id => api.patch(`/notifications/${id}/read`),
  markAllRead: () => api.patch('/notifications/read-all'),
  delete:    id => api.delete(`/notifications/${id}`),
};

// ── Users ─────────────────────────────────────
export const usersAPI = {
  getAll:        p        => api.get('/users', { params: p }),
  getOne:        id       => api.get(`/users/${id}`),
  create:        d        => api.post('/users', d),
  update:        (id, d)  => api.put(`/users/${id}`, d),
  delete:        id       => api.delete(`/users/${id}`),
  resetPassword: (id, pwd) => api.post(`/users/${id}/reset-password`, { password: pwd }),
};

// ── Documents ─────────────────────────────────
export const documentsAPI = {
  getAll:  p        => api.get('/documents', { params: p }),
  upload:  formData => api.post('/documents', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete:  id       => api.delete(`/documents/${id}`),
};

// ── Expenses ──────────────────────────────────
export const expensesAPI = {
  getAll:    p          => api.get('/expenses', { params: p }),
  getSummary: p         => api.get('/expenses/summary', { params: p }),
  create:    formData   => api.post('/expenses', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update:    (id, fd)   => api.put(`/expenses/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete:    id         => api.delete(`/expenses/${id}`),
  import:    data       => api.post('/expenses/import', data),
  issueReceipt: id      => api.post(`/expenses/${id}/receipt`),
};

// ── Budget ────────────────────────────────────
export const budgetAPI = {
  getCategories:     ()        => api.get('/budget/categories'),
  getPlans:          ()        => api.get('/budget/plans'),
  getPlan:           id        => api.get(`/budget/plans/${id}`),
  createPlan:        d         => api.post('/budget/plans', d),
  updatePlan:        (id, d)   => api.put(`/budget/plans/${id}`, d),
  deletePlan:        id        => api.delete(`/budget/plans/${id}`),
  getTransactions:   p         => api.get('/budget/transactions', { params: p }),
  createTransaction: fd        => api.post('/budget/transactions', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateTransaction: (id, fd)  => api.put(`/budget/transactions/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteTransaction: id        => api.delete(`/budget/transactions/${id}`),
  getSummary:        p         => api.get('/budget/summary', { params: p }),
  getForecast:       p         => api.get('/budget/forecast', { params: p }),
  copyPlan:          (id, d)   => api.post(`/budget/plans/${id}/copy`, d),
};

// ── Role Permissions ──────────────────────────
export const rolePermissionsAPI = {
  getAll:  ()           => api.get('/role-permissions'),
  update:  (role, data)  => api.put(`/role-permissions/${role}`, data),
};

// ── Settings (app configuration) ──────────────
export const settingsAPI = {
  get:    ()  => api.get('/settings'),
  update: d   => api.put('/settings', d),
  runRentReminders: (force=false) => api.post('/settings/rent-reminders/run', { force }),
};

// ── Files (S3 signed URLs) ────────────────────
export const filesAPI = {
  getSignedUrl: key => api.get('/files/signed-url', { params: { key } }),
};

// Build a URL usable in <a href> / <img src> that works on all mobile browsers.
// Local paths are returned as-is; S3 keys go through the backend /view redirect
// endpoint which accepts the JWT as a query param (browser links can't send headers).
export const fileViewUrl = (key) => {
  if (!key) return null;
  if (key.startsWith('/')) return key;
  const token = localStorage.getItem('token') || '';
  return `/api/files/view?key=${encodeURIComponent(key)}&token=${encodeURIComponent(token)}`;
};

export default api;
